import { useEffect, useRef } from "react";
import className from "classnames/bind";
import QuizControls from "./QuizControls";
import QuizOptActiveIcon from "@/assets/svg/quiz-opt-active.svg";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
import type { QuizOption, QuizQuestionData } from "./types";

import styles from "./DigitalExhibitQuiz.module.scss";
const cx = className.bind(styles);

type QuizQuestionProps = {
  question: QuizQuestionData;
  current: number;
  // Whether the quiz has been started — the question panel is mounted (inert)
  // behind the landing overlay before that, so focus must wait on this flag
  started: boolean;
  answers: (number | null)[];
  canAdvance: boolean;
  select: (optionIndex: number) => void;
  onNext: () => void;
  onBack: () => void;
};

export default function QuizQuestion({
  question,
  current,
  started,
  answers,
  canAdvance,
  select,
  onNext,
  onBack,
}: QuizQuestionProps) {
  const { labels } = usePageWrapperData();
  const options = (question.options ?? []).filter(Boolean) as QuizOption[];
  const selected = answers[current];
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const promptRef = useRef<HTMLDivElement>(null);

  // Focus follows the step: Start/Next/Back land the screen reader on the
  // question text first (read once, no overlap with the answers), and a
  // single Tab reaches the first answer. Without this the unmounting
  // Start/Next button drops focus to <body> and Tab lands past the quiz.
  // Gated on `started`: this panel is mounted (inert) behind the landing
  // overlay before then, and focusing into an inert subtree silently fails.
  // preventScroll: the intro's own square-up scroll owns the viewport.
  useEffect(() => {
    if (!started) return;
    promptRef.current?.focus({ preventScroll: true });
  }, [started, current]);

  const rovingTabIndex = (i: number) => {
    if (selected != null) return selected === i ? 0 : -1;
    return i === 0 ? 0 : -1;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, i: number) => {
    const len = options.length;
    if (len === 0) return;
    let target = i;
    switch (e.key) {
      case "ArrowUp":
      case "ArrowLeft":
        target = (i - 1 + len) % len;
        break;
      case "ArrowDown":
      case "ArrowRight":
        target = (i + 1) % len;
        break;
      case " ":
      case "Enter":
        e.preventDefault();
        select(i);
        return;
      default:
        return;
    }
    e.preventDefault();
    select(target);
    optionRefs.current[target]?.focus();
  };

  return (
    <div className={cx("dx-quiz__question")} data-quiz-question>
      <div
        ref={promptRef}
        tabIndex={-1}
        role="heading"
        aria-level={2}
        className={cx("dx-quiz__prompt", "dx-subhead")}
        data-quiz-stagger
      >
        {question.questionText}
      </div>

      <div className={cx("dx-quiz__options")} data-quiz-stagger role="radiogroup" aria-label="Answer options">
        {options.map((opt, i) => (
          <button
            key={i}
            ref={(el) => {
              optionRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected === i}
            tabIndex={rovingTabIndex(i)}
            onClick={() => select(i)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            className={cx("dx-quiz__option", { "is-selected": selected === i })}
          >
            <span className={cx("dx-quiz__option-label", "dx-caption")}>{opt.label}</span>
            <span className={cx("dx-quiz__option-dot")} aria-hidden>
              <QuizOptActiveIcon className={cx("dot-icon")} />
            </span>
          </button>
        ))}
      </div>

      <QuizControls
        variant="question"
        onBack={onBack}
        onNext={onNext}
        canAdvance={canAdvance}
        backLabel={labels?.quizBack || undefined}
        nextLabel={labels?.quizNext || undefined}
      />
    </div>
  );
}
